// Savings pieces of the sandbox playground: an account's interest, and
// booking and breaking fixed deposits.
import { useMemo, useState } from "react";
import { Button, Confirm, ErrorNotice, Field, Input, Modal, Money, Panel, Select, useToast } from "../../components/ui";
import { ApiError, type GameApi } from "../../lib/api";
import { newIdempotencyKey } from "../../lib/ids";
import { formatMoney, parseMoney } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { formatGameTime } from "../../lib/time";
import type { Account, Bank, FixedDeposit, MaturityInstruction } from "../../lib/types";
import { pct } from "./Savings";

const INSTRUCTION: Record<MaturityInstruction, string> = {
  payout: "Pay out at maturity",
  rollover_principal: "Roll over the principal",
  rollover_all: "Roll over everything",
};

function scopeNotice(err: unknown) {
  if (err instanceof ApiError && err.code === "insufficient_scope") {
    return <div className="notice warn">This playground key was issued before savings existed. End the session (top right) and start a new one.</div>;
  }
  return null;
}

/** A savings or fixed-deposit account's rate, accrued interest and payouts. */
export function InterestPanel({ api, gameId, account }: { api: GameApi; gameId: string; account: Account }) {
  const q = useQuery(`sandbox:${gameId}:interest:${account.id}`, () => api.accountInterest(account.id));
  const v = q.data;
  const fd = account.account_type === "fixed_deposit";
  return (
    <Panel title="Interest" sub={v ? `${v.product_name} · ${fd ? "paid at maturity" : `paid ${v.payout_frequency}`}` : undefined}>
      {scopeNotice(q.error) ?? <ErrorNotice error={q.error} onRetry={q.reload} />}
      {v && (
        <div className="stack" style={{ gap: 14 }}>
          <div className="row" style={{ gap: 28, flexWrap: "wrap" }}>
            <div><div className="cell-sub">Rate</div><div className="cell-title">{pct(v.rate_bps)} a year</div></div>
            <div><div className="cell-sub">Earned, not yet paid</div><div className="cell-title"><Money minor={v.accrued_interest_minor} currency={account.currency} /></div></div>
            <div><div className="cell-sub">{fd ? "Matures" : "Next payout"}</div><div className="cell-title">{v.next_payout_day}</div></div>
            {v.withdrawals_per_month != null && (
              <div><div className="cell-sub">Withdrawals this month</div><div className="cell-title">{v.withdrawals_this_month} of {v.withdrawals_per_month}</div></div>
            )}
          </div>
          {v.forfeit_month && <div className="notice warn">An extra withdrawal in {v.forfeit_month} means this period's interest will be forfeited.</div>}
          <p className="small muted" style={{ margin: 0 }}>
            Accrued through {v.accrued_through}. Interest is worked out for each completed game day; advance the game clock to see it arrive.
          </p>
          {v.payouts.length > 0 && (
            <table className="data">
              <tbody>
                {v.payouts.slice(0, 6).map((p) => (
                  <tr key={p.transaction_id}>
                    <td>{p.narration}<div className="cell-sub">{formatGameTime(p.game_time)}</div></td>
                    <td className="num"><Money minor={p.gross_minor} currency={account.currency} tone="credit" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Panel>
  );
}

/** Fixed deposits funded from this account. */
export function FixedDepositsPanel({ api, gameId, account }: { api: GameApi; gameId: string; account: Account }) {
  const toast = useToast();
  const q = useQuery(`sandbox:${gameId}:fds:${account.id}`, () => api.fixedDeposits({ source_account_id: account.id }));
  const [breaking, setBreaking] = useState<FixedDeposit | null>(null);
  const quote = useQuery(breaking ? `sandbox:${gameId}:break-quote:${breaking.id}` : null, () => api.breakQuote(breaking!.id));
  const key = useMemo(() => newIdempotencyKey("break"), [breaking]);
  const brk = useAction(() => api.breakFixedDeposit(breaking!.id, quote.data!.penalty_minor, key));
  const rows = q.data?.data ?? [];
  if (q.error && !(q.error instanceof ApiError && q.error.code === "insufficient_scope")) return null;
  if (!q.data || rows.length === 0) return null;
  const cur = account.currency;
  return (
    <Panel title="Fixed deposits" sub="Booked from this account. Matured and broken deposits pay back here." flush>
      <table className="data">
        <tbody>
          {rows.map((d) => (
            <tr key={d.id}>
              <td>
                <div className="cell-title">{d.product_name} · {d.term_days} days at {pct(d.rate_bps)}</div>
                <div className="cell-sub">
                  <span className={`badge ${d.status === "active" ? "ok" : d.status === "broken" ? "bad" : ""}`}>{d.status}</span>{" "}
                  {d.status === "active" ? `matures ${d.maturity_day} · ${INSTRUCTION[d.maturity_instruction].toLowerCase()}` : `${formatMoney(d.interest_paid_minor, cur)} interest paid`}
                  {d.terms_completed > 0 && d.status === "active" ? ` · term ${d.terms_completed + 1}` : ""}
                </div>
              </td>
              <td className="num">
                <Money minor={d.status === "active" ? d.balance_minor : d.principal_minor} currency={cur} />
                {d.status === "active" && d.accrued_interest_minor > 0 && <div className="cell-sub">+{formatMoney(d.accrued_interest_minor, cur)} earned</div>}
              </td>
              <td className="num">{d.status === "active" && <Button size="small" onClick={() => setBreaking(d)}>Break early</Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Confirm open={!!breaking} title="Break this fixed deposit early?" danger confirmLabel="Break deposit"
        busy={brk.pending} error={brk.error ?? quote.error} disabled={!quote.data}
        body={quote.data ? (
          <div className="stack" style={{ gap: 6 }}>
            <div className="row" style={{ justifyContent: "space-between" }}><span>Principal</span><span>{formatMoney(quote.data.principal_minor, cur)}</span></div>
            <div className="row" style={{ justifyContent: "space-between" }}><span>Interest earned</span><span>{formatMoney(quote.data.accrued_interest_minor, cur)}</span></div>
            <div className="row" style={{ justifyContent: "space-between" }}><span>Interest given up</span><span>−{formatMoney(quote.data.forfeited_interest_minor, cur)}</span></div>
            {quote.data.tax_minor > 0 && <div className="row" style={{ justifyContent: "space-between" }}><span>Tax withheld</span><span>−{formatMoney(quote.data.tax_minor, cur)}</span></div>}
            <div className="row" style={{ justifyContent: "space-between" }}><span>Early-withdrawal penalty</span><span>−{formatMoney(quote.data.penalty_minor, cur)}</span></div>
            <div className="row" style={{ justifyContent: "space-between", fontWeight: 600 }}><span>Paid back</span><span>{formatMoney(quote.data.payout_minor, cur)}</span></div>
          </div>
        ) : <>Working out the figures…</>}
        onConfirm={async () => {
          if (await brk.run()) {
            setBreaking(null);
            invalidate(`sandbox:${gameId}`);
            toast("Fixed deposit broken");
          }
        }}
        onClose={() => setBreaking(null)} />
    </Panel>
  );
}

export function OpenFixedDeposit({ onClose, api, gameId, account, bank }: { onClose: () => void; api: GameApi; gameId: string; account: Account; bank: Bank }) {
  const toast = useToast();
  const products = useQuery(`sandbox:${gameId}:deposit-products:${bank.id}`, () => api.depositProducts(bank.id));
  const fds = (products.data?.data ?? []).filter((p) => p.kind === "fixed_deposit" && p.status === "active");
  const [productId, setProductId] = useState("");
  const product = fds.find((p) => p.id === productId) ?? fds[0];
  const [term, setTerm] = useState(0);
  const termDays = product?.terms.some((t) => t.days === term) ? term : product?.terms[0]?.days ?? 0;
  const [amount, setAmount] = useState("");
  const [instruction, setInstruction] = useState<MaturityInstruction | "">("");
  const minor = parseMoney(amount, account.currency.exponent);
  const quote = useQuery(product && minor ? `sandbox:${gameId}:fd-quote:${product.id}:${termDays}:${minor}` : null,
    () => api.quoteDeposit({ product_id: product!.id, amount_minor: minor!, term_days: termDays }));
  const key = useMemo(() => newIdempotencyKey("fd"), []);
  const open = useAction(() => api.openFixedDeposit({ source_account_id: account.id, product_id: product!.id, term_days: termDays, amount_minor: minor!,
    maturity_instruction: instruction || undefined }, key));
  const cur = account.currency;
  return (
    <Modal open title="Book a fixed deposit" onClose={onClose} description="Moves the money from this account into a deposit at the term's rate, locked until maturity."
      onSubmit={async (e) => {
        e.preventDefault();
        if (await open.run()) {
          invalidate(`sandbox:${gameId}`);
          toast(`Booked ${formatMoney(minor!, cur)} for ${termDays} days`);
          onClose();
        }
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={open.pending} disabled={!product || !minor}>Book deposit</Button></>}>
      <div className="form">
        {scopeNotice(products.error ?? open.error)}
        {products.data && fds.length === 0 && <div className="notice">Add a fixed-deposit product in the Savings tab first.</div>}
        {fds.length > 1 && (
          <Field label="Product">
            <Select value={product?.id} onChange={(e) => setProductId(e.target.value)}>{fds.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
          </Field>
        )}
        {product && (
          <>
            <Field label="Term">
              <Select value={termDays} onChange={(e) => setTerm(Number(e.target.value))}>
                {product.terms.map((t) => <option key={t.days} value={t.days}>{t.days} game days at {pct(t.rate_bps)}</option>)}
              </Select>
            </Field>
            <Field label={`Amount (${cur.code})`} hint={product.min_opening_minor > 0 ? `At least ${formatMoney(product.min_opening_minor, cur)}.` : undefined}>
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="50,000" />
            </Field>
            <Field label="At maturity">
              <Select value={instruction || product.default_maturity_instruction} onChange={(e) => setInstruction(e.target.value as MaturityInstruction)}>
                {(Object.keys(INSTRUCTION) as MaturityInstruction[]).map((k) => <option key={k} value={k}>{INSTRUCTION[k]}</option>)}
              </Select>
            </Field>
            {quote.data && (
              <div className="preview">
                <span>Earns {formatMoney(quote.data.net_interest_minor, cur)}{quote.data.tax_minor > 0 ? ` after ${formatMoney(quote.data.tax_minor, cur)} tax` : ""}; </span>
                <span>{formatMoney(minor! + quote.data.net_interest_minor, cur)} at maturity.</span>
              </div>
            )}
          </>
        )}
        <ErrorNotice error={open.error} />
      </div>
    </Modal>
  );
}
